import PropTypes from 'prop-types';
import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';

// components
import CheckboxWithoutForm from '@atoms/CheckboxWithoutForm';
import RadioWithoutForm from '@atoms/RadioWithoutForm';
import TooltipInfo from '@atoms/TooltipInfo/TooltipInfo';
import { BodyMedium, LinkSmall } from '@styled';

// functionality
import { CartItemMembershipContext } from './CartItemMembershipProvider';
import { LoadingContext } from '@context/LoadingProvider';
import { createCartItem, deleteCartItem, replaceCartItem } from '@general/api/cartAPI';
import { formatMoney } from '@general/helpers/currency.helpers';
import productTypes from '@general/productTypes';
import useCartFromAPI from '@hooks/apiLegacy/useCartFromAPI';
import useMembershipDuesYears from '@hooks/useCartDuesYears';
import useViewer from '@hooks/useViewer';
import colors from '@general/colors';

const DonationTitleStyled = styled(LinkSmall)`
    color: black !important;
    cursor: default;
    display: block;
`;

const CheckboxWrapperStyled = styled.div`
    display: flex;
    column-gap: 1rem;
`;

const TooltipTextPersonal = 'This contribution is personal and with a non-corporate credit card.';
const TooltipTextCorporate = 'This contribution is with a corporate credit card.';

const CartItemMembershipPac = ({ pacFees }) => {
    // -----------------------------
    // State
    // -----------------------------

    const { setIsLoading } = useContext(LoadingContext);
    const { membershipDataForMember, membershipCartItem } = useContext(CartItemMembershipContext);
    const [cartItem, setCartItem] = useState();
    const [applicableFees, setApplicableFees] = useState();
    const [applicableFeeCorporate, setApplicableFeeCorporate] = useState();
    const [applicableFeePersonal, setApplicableFeePersonal] = useState();

    // -----------------------------
    // Hooks
    // -----------------------------

    const { viewer } = useViewer();
    const { cart, refetch } = useCartFromAPI({ viewerId: viewer?.recordId });
    const { oneOrThreeYears } = useMembershipDuesYears({
        memberId: membershipDataForMember.memberId,
        duesProductId: membershipCartItem.product_id,
    });

    // -----------------------------
    // Effects
    // -----------------------------

    useEffect(() => {
        if (!membershipDataForMember) {
            return;
        }

        setCartItem(membershipDataForMember.dues.find(item => item.product_type === productTypes.PAC));
    }, [membershipDataForMember]);

    useEffect(() => {
        if (!pacFees || !oneOrThreeYears) {
            return;
        }

        setApplicableFees(pacFees.filter(fee => fee.years === oneOrThreeYears));
        setApplicableFeeCorporate(pacFees.find(fee => fee.years === oneOrThreeYears && fee.corporate));
        setApplicableFeePersonal(pacFees.find(fee => fee.years === oneOrThreeYears && !fee.corporate));
    }, [pacFees, oneOrThreeYears]);

    // -----------------------------
    // Helpers
    // -----------------------------

    const addPacToCart = async () => {
        setIsLoading(true);

        const cartItemData = [
            {
                member_id: membershipDataForMember.memberId,
                product_id: applicableFeePersonal.id,
                quantity: 1,
                meta_info: {
                    member_id: membershipCartItem.meta_info.member_id,
                    member_name: membershipCartItem.meta_info.member_name,
                    company_name: membershipCartItem.meta_info.company_name,
                    product_id: applicableFeePersonal.id,
                    product_code: applicableFeePersonal.product_code,
                    donation_description: applicableFeePersonal.description,
                    donation_years: applicableFeePersonal.years,
                    corporate: false,
                },
            },
        ];

        const updatedCart = await createCartItem({
            cartId: cart.id,
            cartItemData,
            viewerId: viewer.recordId,
        });

        await refetch();

        setCartItem(
            updatedCart.items.find(registeredSession => registeredSession.product_id === applicableFeePersonal.id)
        );

        setIsLoading(false);
    };

    const removePacFromCart = async () => {
        setIsLoading(true);

        await deleteCartItem({
            cartId: cart.id,
            cartItemId: cartItem.id,
            viewerId: viewer.recordId,
        });

        await refetch();

        setIsLoading(false);
    };

    const toggleCorporateOrPersonal = async corporateOrPersonal => {
        if (!cartItem) {
            return;
        }

        const isCorporate = corporateOrPersonal === 'c';
        const pacToUse = pacFees.find(pac => pac.years === oneOrThreeYears && pac.corporate === isCorporate);
        const newPacItem = { ...cartItem, ...pacToUse };
        newPacItem.product_id = pacToUse.id;
        delete newPacItem.id;

        try {
            setIsLoading(true);
            await replaceCartItem({
                cartId: cart.id,
                cartItemId: cartItem.id,
                cartItemData: newPacItem,
                viewerId: viewer.recordId,
            });
        } catch {
            alert('Error toggling Corporate/Personal');
        } finally {
            setIsLoading(false);
        }
    };

    // -----------------------------
    // Render
    // -----------------------------

    if (!cart || !applicableFees) {
        return null;
    }

    const selectedPacFee = applicableFees.find(fee => fee.id === cartItem?.product_id);

    return (
        <div>
            <div className="flex justify-between">
                <CheckboxWrapperStyled>
                    <CheckboxWithoutForm
                        checked={!!cartItem}
                        onCheckChange={checked => {
                            if (checked) {
                                addPacToCart();
                            } else {
                                removePacFromCart();
                            }
                        }}
                    >
                        <DonationTitleStyled as="span">
                            Add a {oneOrThreeYears}-Year Political Action Committee Contribution
                        </DonationTitleStyled>
                    </CheckboxWithoutForm>
                    {!!selectedPacFee && (
                        <TooltipInfo
                            color={colors.azure}
                            text={selectedPacFee.corporate ? TooltipTextCorporate : TooltipTextPersonal}
                            tooltipClasses="ml-1rem"
                        />
                    )}
                </CheckboxWrapperStyled>

                <div className="ml-1.5rem">
                    <BodyMedium>{formatMoney(applicableFeePersonal.price, cart.locale)}</BodyMedium>
                </div>
            </div>

            {!!cartItem && (
                <div style={{ marginLeft: '28px' }}>
                    {applicableFees.map(fee => (
                        <div key={fee.id} className="mb-0.5rem">
                            <RadioWithoutForm
                                checked={fee.id === cartItem.product_id}
                                label={fee.corporate ? 'Corporate' : 'Personal'}
                                name="pac"
                                value={fee.corporate ? 'c' : 'p'}
                                onChange={value => toggleCorporateOrPersonal(value)}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

CartItemMembershipPac.propTypes = {
    pacFees: PropTypes.arrayOf(
        PropTypes.shape({
            //
        })
    ),
};

export default CartItemMembershipPac;
